import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { cn } from '../utils/cn';
import { useLanguage } from '../contexts/LanguageContext';

// Clean assets
const ASSETS = {
    humanHead: '/assets/human_head.png',
    robotHead: '/assets/robot_head.png',
    mainFace: '/assets/mainscreenface.png', // New Asset
    league: {
        wooden: '/assets/league_wooden.png',
        bronze: '/assets/league_bronze.png',
        silver: '/assets/league_silver.png',
        gold: '/assets/league_gold.png',
    }
};

export const WelcomeScreen = ({ onStartGame, onShowRating, onShowRules, onShowProfile }) => {
    const { t } = useLanguage();
    const [userProfile, setUserProfile] = useState(null);

    // Mock data for immediate "Wow" factor while loading
    const tg = window.Telegram?.WebApp;
    const user = tg?.initDataUnsafe?.user;
    const userName = user?.first_name || user?.username || t('guest_player');
    const userPhotoUrl = user?.photo_url;

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const profile = await apiClient.getUserProfile();
                setUserProfile(profile);
            } catch (error) {
                console.error('Failed to load user profile:', error);
                setUserProfile({ rating: 1250, league: 'silver' });
            }
        };
        fetchProfile();
    }, [t]);

    const leagueKey = userProfile?.league?.toLowerCase() || 'bronze';
    // Use translations for league names if possible, or fallback to uppercase key
    const leagueName = t(`league_${leagueKey}`) || (userProfile?.league ? userProfile.league.toUpperCase() : 'BRONZE');

    // Preload assets for instant display
    useEffect(() => {
        const imagesToPreload = [
            ...Object.values(ASSETS.league),
            ASSETS.mainFace
        ];
        imagesToPreload.forEach(src => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    return (
        <div className="flex flex-col h-full w-full px-6 pt-2 pb-1 gap-4 justify-between overflow-hidden">

            {/* 1. Glass Profile Card - With Arrow */}
            <button
                onClick={() => onShowProfile()}
                className="w-full text-left bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-4 shadow-2xl relative overflow-hidden group active:scale-98 transition-transform shrink-0"
            >
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />

                <div className="flex items-center justify-between relative z-10 w-full">
                    <div className="flex items-center gap-4">
                        {/* Avatar */}
                        <div className="relative shrink-0">
                            <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-900 border border-white/10">
                                <img
                                    src={userPhotoUrl || ASSETS.humanHead}
                                    alt="Profile"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>

                        {/* Info */}
                        <div className="flex flex-col gap-0.5">
                            <h2 className="text-lg font-bold text-white tracking-tight leading-tight">{userName}</h2>
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 bg-cyan-950/50 px-2 py-0.5 rounded-full border border-cyan-500/20">
                                    <img
                                        src={ASSETS.league[leagueKey] || ASSETS.league.bronze}
                                        alt="League"
                                        className="w-3 h-3 object-contain"
                                    />
                                    <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wide">
                                        {leagueName}
                                    </span>
                                </div>

                                <span className="text-slate-400 text-xs flex items-center gap-1 font-mono">
                                    🏆 {userProfile?.rating || '---'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right Arrow Icon - Strictly Centered */}
                    <div className="w-8 h-8 flex flex-col items-center justify-center rounded-full bg-white/5 border border-white/10 text-slate-400 group-hover:text-white group-hover:bg-white/10 transition-colors shrink-0">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-0.5">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </div>
                </div>
            </button>

            {/* 2. Main Visual / Split Face with Tighter Water Ripple Effect */}
            <div className="flex-1 flex flex-col items-center justify-center relative min-h-0 translate-y-2.5">
                {/* Central "Pulse" Visual */}
                {/* Reduced container size to contain ripple start point better */}
                <div className="relative w-48 h-48 flex items-center justify-center">
                    {/* Ripples - Using custom animate-ripple for smaller spread (1.35x scale instead of 2x) */}
                    <div className="absolute inset-0 rounded-full border border-cyan-500/40 animate-ripple" style={{ animationDelay: '0s' }} />
                    <div className="absolute inset-0 rounded-full border border-cyan-500/30 animate-ripple" style={{ animationDelay: '1s' }} />
                    <div className="absolute inset-0 rounded-full border border-cyan-500/20 animate-ripple" style={{ animationDelay: '2s' }} />

                    {/* Main Face Container */}
                    <div className="w-48 h-48 rounded-full bg-slate-900 border border-white/10 shadow-2xl flex items-center justify-center relative z-10 overflow-hidden shrink-0">
                        <img
                            src={ASSETS.mainFace}
                            alt="Human or Bot"
                            className="w-full h-full object-cover transform scale-105"
                        />
                    </div>
                </div>

                <p className="mt-4 text-center text-slate-400 text-sm max-w-[240px] leading-relaxed shrink-0">
                    {t('welcome_cta')}<br />
                    <span className="text-white font-semibold glow-sm">{t('welcome_subtitle')}</span>
                </p>
            </div>
            {/* 3. Actions */}
            <div className="flex flex-col gap-3 w-full">
                <Button variant="primary" size="xl" onClick={onStartGame} className="w-full font-black tracking-widest text-lg">
                    {t('start_game')}
                </Button>

                <div className="flex gap-3">
                    <Button variant="secondary" className="flex-1" onClick={onShowRating}>
                        {t('rating')}
                    </Button>
                    <Button variant="secondary" className="flex-1" onClick={onShowRules}>
                        {t('how_to_play')}
                    </Button>
                </div>
            </div>

        </div>
    );
};
